import { useNavigate } from "react-router-dom";
import { STATUS_META } from "../constants/status";
import { t } from "../constants/i18n";
import type { Lang } from "../constants/i18n";
import type { Project } from "../constants/projects";
import type { Product } from "../constants/products";
import type { MediaItem } from "../constants/types";
import "./ProjectPage.css";

type Entry = Project | Product;

interface ProjectPageProps {
  lang: Lang;
  entry: Entry;
  backTo: string;
  backLabel: string;
}

const GithubIcon = ({ color }: { color: string }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" />
  </svg>
);

const GitlabIcon = ({ color }: { color: string }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <path d="M23.955 13.587l-1.342-4.135-2.664-8.189a.455.455 0 00-.867 0L16.418 9.45H7.582L4.918 1.263a.455.455 0 00-.867 0L1.386 9.45.044 13.587a.924.924 0 00.331 1.023L12 23.054l11.625-8.443a.92.92 0 00.33-1.024" />
  </svg>
);

const ExternalIcon = ({ color }: { color: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

export default function ProjectPage({ lang, entry, backTo, backLabel }: ProjectPageProps) {
  const navigate = useNavigate();
  const statusMeta = STATUS_META[entry.status];
  const sc = statusMeta.color;
  const statusLabel = lang === "jp" ? statusMeta.label_jp : statusMeta.label_en;

  const liveUrl = "live" in entry ? entry.live : undefined;
  const isPrivate = "isPrivate" in entry ? entry.isPrivate : false;
  const hasLinks = !isPrivate && (entry.github || entry.gitlab || liveUrl);
  const hasMedia = entry.media && entry.media.length > 0;
  const longDesc = entry.longDesc?.[lang] ?? entry.longDesc?.en;

  // GitHub button swaps black/white based on --gh-bg / --gh-color, set per-theme in useTheme.ts
  const ghColor = "var(--gh-color)";
  const liveColor = "var(--bg)";
  const glColor = "#ffffff";

  return (
    <div className="pp">
      <div className="pp-hero">
        <div className="container">
          <button className="pp-back" onClick={() => navigate(backTo)}>
            ← {backLabel}
          </button>

          <div className="pp-hero-top">
            <div>
              <h1 className="pp-title">{entry.name}</h1>
              <div className="pp-meta">
                <span className="pp-year">{entry.year}</span>
                <div className="pp-tags">
                  {entry.tags.map((tag) => (
                    <span key={tag} className="pp-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <span className="pp-status" style={{ color: sc, borderColor: sc + "55" }}>
              {statusLabel}
            </span>
          </div>

          {hasLinks && (
            <div className="pp-links">
              {liveUrl && (
                <a className="pp-btn pp-btn-live" href={liveUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalIcon color={liveColor} />
                  live
                </a>
              )}
              {entry.github && (
                <a className="pp-btn pp-btn-github" href={entry.github} target="_blank" rel="noopener noreferrer">
                  <GithubIcon color={ghColor} />
                  github
                </a>
              )}
              {entry.gitlab && (
                <a className="pp-btn pp-btn-gitlab" href={entry.gitlab} target="_blank" rel="noopener noreferrer">
                  <GitlabIcon color={glColor} />
                  gitlab
                </a>
              )}
            </div>
          )}
          {isPrivate && (
            <div className="pp-links">
              <span className="pp-private">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                {t(lang, "card_private")}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="pp-body">
        <div className="container">
          <div className={`pp-layout${!hasMedia ? " full" : ""}`}>
            <div>
              <p className="pp-desc-short">{entry.desc[lang] ?? entry.desc.en}</p>
              {longDesc && <p className="pp-desc-long">{longDesc}</p>}
            </div>

            {hasMedia && (
              <div>
                <div className="pp-media">
                  {(entry.media as MediaItem[]).map((m, i) => (
                    <div className="pp-media-item" key={i}>
                      {m.type === "image" ? (
                        <img className="pp-media-img" src={m.src} alt={m.alt ?? entry.name} loading="lazy" />
                      ) : (
                        <video className="pp-media-vid" src={m.src} poster={m.poster} controls preload="metadata" />
                      )}
                    </div>
                  ))}
                </div>
                <div className="pp-info">
                  <div className="pp-info-row">
                    <span className="pp-info-key">year</span>
                    <span className="pp-info-val">{entry.year}</span>
                  </div>
                  <div className="pp-info-row">
                    <span className="pp-info-key">status</span>
                    <span className="pp-info-val" style={{ color: sc }}>
                      {statusLabel}
                    </span>
                  </div>
                  <div className="pp-info-row">
                    <span className="pp-info-key">stack</span>
                    <span className="pp-info-val">{entry.tags.join(", ")}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
